/**
 * Central Oversættelsesfil
 * Oversætter alle dimension navne og labels fra engelsk til dansk
 */

/**
 * Oversæt dimension navn til dansk
 * @param {string} dimension - Dimension navn (fx "contact_rhythm")
 * @returns {string} - Oversat dimension navn (fx "Kontaktrytme")
 */
export function translateDimension(dimension) {
  const translations = {
    // Conflict Core
    'avoid_contempt_generalizations': 'Undgå Foragt og Generaliseringer',
    'reduce_defensiveness': 'Reducer Forsvarsreaktioner',
    'complaint_vs_criticism': 'Klage Mod Kritik',
    'self_awareness_arousal': 'Selvbevidsthed og Arousal',
    'accept_pause': 'Accepter Pause',
    'pause_signal': 'Pause Signal',
    'resume_rule': 'Genoptagelses Regel',
    'one_topic': 'Ét Emne Ad Gangen',
    'feelings_without_blame': 'Følelser Uden Skyld',
    'repair_attempts': 'Reparationsforsøg',
    'post_conflict_debrief': 'Efter-Konflikt Gennemgang',
    'one_next_step': 'Ét Næste Skridt',
    'separate_person_problem': 'Adskil Person og Problem',
    'listen_before_reply': 'Lyt Før Svar',
    'validate_without_agreeing': 'Valider Uden At Være Enig',
    'timing_rule': 'Timing Regel',
    'problem_solving_structure': 'Problemløsnings Struktur',
    'reconnect_ritual': 'Genkontakt Ritual',
    
    // Expectations Core
    'contact_rhythm': 'Kontaktrytme',
    'conflict_addressing': 'Konflikt Håndtering',
    'conflict_pause_norm': 'Konflikt Pause Norm',
    'repair_norm': 'Reparations Norm',
    'respect_under_stress': 'Respekt Under Stress',
    'roles_and_responsibility': 'Roller og Ansvar',
    'fairness_model': 'Fairheds Model',
    'money_norms': 'Penge Normer',
    'planning_style': 'Planlægnings Stil',
    'alone_time_legitimacy': 'Alene Tid Legitimitet',
    'relationship_priority_in_stress': 'Relations Prioritet Under Stress',
    'daily_appreciation': 'Daglig Værdsættelse',
    'screen_free_time': 'Skærmfri Tid',
    'privacy_and_sharing': 'Privatliv og Deling',
    'public_support_private_feedback': 'Offentlig Støtte, Privat Feedback',
    'hard_talks_without_blame': 'Svære Samtaler Uden Skyld',
    'regular_checkins': 'Regelmæssige Check-ins',
    'external_boundaries': 'Eksterne Grænser',
    
    // Communication Core (allerede på dansk, men inkluderet for konsistens)
    'kommunikation_stil': 'Kommunikations Stil',
    'kommunikation_aktiv_lytning': 'Aktiv Lytning',
    'kommunikation_ansvar': 'Kommunikations Ansvar',
    'kommunikation_forståelse': 'Kommunikations Forståelse',
    'kommunikation_respekt': 'Kommunikations Respekt',
    'kommunikation_validering': 'Kommunikations Validering',
    'kommunikation_behov': 'Kommunikations Behov',
    'kommunikation_tempo': 'Kommunikations Tempo',
    'kommunikation_klarhed': 'Kommunikations Klarhed',
    
    // Boundaries Core
    'public_conflict_boundary': 'Offentlig Konflikt Grænse',
    'pause_right': 'Ret til Pause',
    'no_contempt': 'Ingen Foragt',
    'private_feedback': 'Privat Feedback',
    'privacy_sharing_boundary': 'Privatlivs Delings Grænse',
    'no_justify_no': 'Ingen Retfærdiggørelse af Nej',
    'alone_time_boundary': 'Alene Tid Grænse',
    'contact_time_boundary': 'Kontakt Tid Grænse',
    'screen_boundary': 'Skærm Grænse',
    'agreement_respect': 'Aftale Respekt',
    'third_party_boundary': 'Tredjepart Grænse',
    'no_stonewalling': 'Ingen Tavshedsmur',
    'tone_stop_boundary': 'Tone Stop Grænse',
    'late_night_boundary': 'Sene Nætter Grænse',
    'values_respect': 'Værdier Respekt',
    'disclosure_pace': 'Åbenheds Tempo',
    'money_boundary': 'Penge Grænse',
    'repair_over_punishment': 'Reparation Fremfor Straf',
    
    // Intimacy Core
    'safe_no': 'Trygt Nej',
    'safe_maybe': 'Trygt Måske',
    'wishes_not_demands': 'Ønsker Ikke Krav',
    'boundaries_no_shame': 'Grænser Uden Skam',
    'touch_without_sex': 'Berøring Uden Sex',
    'initiation_kindness': 'Initiering med Venlighed',
    'slow_down': 'Sænk Farten',
    'weekly_closeness_time': 'Ugentlig Nærheds Tid',
    'libido_drivers': 'Libido Drivere',
    'positive_feedback': 'Positiv Feedback',
    'digital_boundaries': 'Digitale Grænser',
    'frequency_expectations': 'Frekvens Forventninger',
    'romance_vs_sex': 'Romantik Mod Sex',
    'stop_anytime': 'Stop Når Som Helst',
    'sensate_focus_openness': 'Sensat Fokus Åbenhed',
    'shame_talk': 'Skam Samtale',
    'ynm_intimacy_lists': 'Ja/Måske/Nej Intimitet Lister',
    'renegotiate_over_time': 'Genforhandl Over Tid',
    
    // Trust Repair Core
    'acknowledge_hurt': 'Anerkend Smerte',
    'own_part': 'Eget Ansvar',
    'temporary_transparency': 'Midlertidig Gennemsigtighed',
    'behavioral_commitments': 'Adfærds Forpligtelser',
    'consistency_over_time': 'Konsistens Over Tid',
    'answering_questions': 'Besvare Spørgsmål',
    'effective_apology': 'Effektiv Undskyldning',
    'allow_time': 'Tillad Tid',
    'trust_checkins': 'Tillids Check-ins',
    'respect_triggers': 'Respekter Triggers',
    'change_routines': 'Ændre Rutiner',
    'transparency_not_control': 'Gennemsigtighed Ikke Kontrol',
    'define_trust': 'Definer Tillid',
    'repair_actions': 'Reparations Handlinger',
    'breach_protocol': 'Brud Protokol',
    'communication_reliability': 'Kommunikations Pålidelighed',
    'listen_to_concerns': 'Lyt til Bekymringer',
    'rebuild_plan': 'Genopbygnings Plan',
    
    // Shared Direction Core
    'stability_first': 'Stabilitet Først',
    'freedom_priority': 'Friheds Prioritet',
    'family_priority': 'Familie Prioritet',
    'career_priority': 'Karriere Prioritet',
    'short_term_plan': 'Kortsigtet Plan',
    'team_decisions': 'Team Beslutninger',
    'location_vision': 'Lokations Vision',
    'money_principles': 'Penge Principper',
    'experiences_over_things': 'Oplevelser Fremfor Ting',
    'shared_rituals': 'Fælles Ritualer',
    'healthy_compromise': 'Sundt Kompromis',
    'support_individual_dreams': 'Støt Individuelle Drømme',
    'parenting_alignment': 'Forældreskab Alignment',
    'external_alignment': 'Ekstern Alignment',
    'growth_orientation': 'Vækst Orientering',
    'meaning_spirituality': 'Mening og Spiritualitet',
    'lifestyle_alignment': 'Livsstil Alignment',
    'hard_choices_team': 'Svære Valg Som Team',
    
    // Emotional Needs Core
    'seen_daily': 'Set Dagligt',
    'comfort_before_solutions': 'Trøst Før Løsninger',
    'curiosity_for_feelings': 'Nysgerrighed på Følelser',
    'safe_vulnerability': 'Tryg Sårbarhed',
    'effort_recognition': 'Indsats Anerkendelse',
    'hard_talk_safety': 'Svære Samtaler Tryghed',
    'touch_as_support': 'Berøring Som Støtte',
    'repair_distance': 'Reparations Afstand',
    'ask_for_support': 'Bed om Støtte',
    'stress_co_regulation': 'Stress Co-Regulering',
    'warmth_in_disagreement': 'Varme i Uenighed',
    'shame_safe_talk': 'Skam Tryg Samtale',
    'reassurance': 'Forsikring',
    'no_stonewalling': 'Ingen Tavshedsmur',
    'share_positive_affect': 'Del Positiv Affekt',
    'hurt_acknowledgement': 'Smerte Anerkendelse',
    'weekly_connection_time': 'Ugentlig Forbindelses Tid',
    'needs_not_demands': 'Behov Ikke Krav',
    
    // Care and Safety
    'emotional_availability': 'Følelsesmæssig Tilgængelighed',
    'comfort': 'Trøst',
    'emotional_validation': 'Følelsesmæssig Validering',
    'acceptance': 'Accept',
    'predictability': 'Forudsigelighed',
    'care_actions': 'Omsorgs Handlinger',
    'repair_attempts': 'Reparationsforsøg',
    'comfortable_silence': 'Komfortabel Tavshed',
    'unconditional_acceptance': 'Betingelsesløs Accept',
    'unconditional_care': 'Betingelsesløs Omsorg',
    
    // Conflict in Family
    'direct_conflict': 'Direkte Konflikt',
    'escalation': 'Eskalering',
    'respectful_conflict': 'Respektfuld Konflikt',
    'listening': 'Lytning',
    'children_protection': 'Børn Beskyttelse',
    'repair_initiative': 'Reparations Initiativ',
    'pause_respect': 'Pause Respekt',
    'conflict_boundaries': 'Konflikt Grænser',
    'responsibility': 'Ansvar',
    'situation_focus': 'Situation Fokus',
    
    // Professional Boundaries
    'time_respect': 'Tid Respekt',
    'workload_fairness': 'Arbejdsbyrde Fairhed',
    'boundary_assertiveness': 'Grænse Selvtillid',
    'professional_conflict': 'Professionel Konflikt',
    'role_clarity': 'Rolle Klarhed',
    'constructive_feedback': 'Konstruktiv Feedback',
    'speech_safety': 'Tale Tryghed',
    'privacy_respect': 'Privatliv Respekt',
    'fair_treatment': 'Fair Behandling',
    'support_availability': 'Støtte Tilgængelighed',
    
    // Roles and Responsibility Family
    'invisible_work_recognition': 'Usynligt Arbejde Anerkendelse',
    'mental_load_distribution': 'Mental Byrde Fordeling',
    'role_flexibility': 'Rolle Fleksibilitet',
    'expectation_clarity': 'Forventnings Klarhed',
    'compensation_burden': 'Kompensation Byrde',
    'shared_decision_making': 'Fælles Beslutningstagning',
    'age_appropriate_responsibility': 'Alderstilpasset Ansvar',
    'care_practical_balance': 'Omsorg Praktisk Balance',
    'responsibility_clarity': 'Ansvar Klarhed',
    
    // Fallback for unknown dimensions
  }
  
  // Return translation or formatted fallback
  if (translations[dimension]) {
    return translations[dimension]
  }
  
  // Fallback: format dimension name nicely
  return dimension
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}

/**
 * Oversæt UI labels
 */
export const UI_LABELS = {
  'Overall Match': 'Samlet Match',
  'Areas for Growth': 'Områder til Udvikling',
  'Feedback Items': 'Feedback Punkter',
  'Match per Dimension': 'Match pr. Dimension',
  'Feedback & Next Steps': 'Feedback & Næste Skridt',
  'How to Use This': 'Hvordan Bruger Du Dette?',
  'High scores (green)': 'Høje scores (grønne)',
  'Low scores (red)': 'Lave scores (røde)',
  'Medium scores (yellow)': 'Medium scores (gule)',
  'Save Evaluation': 'Gem Evaluering',
  'Go to Dashboard': 'Gå til Dashboard',
  'Create New Card': 'Lav Nyt Kort',
  'Test Your Card': 'Test Dit Kort',
  'See Match Score and Feedback': 'Se Match Score og Feedback'
}

/**
 * Oversæt UI label
 * @param {string} label - UI label på engelsk
 * @returns {string} - Oversat label på dansk
 */
export function translateLabel(label) {
  return UI_LABELS[label] || label
}
